/* eslint-disable react/no-array-index-key */
import { ComponentType, memo, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Grid, Paper, Stack } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { ISurveyTypeProps } from 'interface/project/projectInfo';
import { useAppDispatch } from 'store';
import { showMessage } from 'store/message/messageSlice';
import { useProject } from 'hooks/useProject';
import { closeDialog, openDialog } from 'store/dialog/dialogSlice';
import InfoDialog from 'components/dialog/InfoDialog';
import SurveySA from './SurveySA';
import SurveyOC from './SurveyOC';
import SurveyDA from './SurveyDA';
import SurveyMC from './SurveyMC';
import SurveyLK from './SurveyLK';

const questionTypeMap: Record<string, ComponentType<ISurveyTypeProps>> = {
  OC: SurveyOC,
  SA: SurveySA,
  DA: SurveyDA,
  MC: SurveyMC,
  LK: SurveyLK,
};
function Stepper3(): JSX.Element {
  const dispatchApp = useAppDispatch();
  const { dispatch, inputs, onChange } = useProjectInfoProvider();
  const { getProjectIdDetail } = useProject();
  const { data, mutate } = getProjectIdDetail(inputs.projectId || '');
  const { stepper3 } = inputs;
  const { questions, noAdditional } = stepper3;

  const surveyAdd = useMemo(() => {
    return data?.surveyAdd || {};
  }, [data?.surveyAdd]);

  useEffect(() => {
    mutate();
  }, [mutate]);

  const updateStepperQuestion = useCallback(() => {
    dispatch({
      type: 'handleQuestionLoadData',
      payload: {
        type: 'inputs',
        questions: surveyAdd?.questions || [],
      },
    });
  }, [dispatch, surveyAdd?.questions]);

  useEffect(() => {
    updateStepperQuestion();
  }, [updateStepperQuestion]);

  useEffect(() => {
    if (noAdditional && questions.length > 0) {
      dispatch({
        type: 'handleQuestionLoadData',
        payload: {
          type: 'inputs',
          questions: [],
        },
      });
    }
  }, [dispatch, noAdditional, questions.length]);

  const handleAdd = (questionType: string) => {
    if (noAdditional) {
      dispatchApp(
        showMessage({
          message: '"추가문항 없음"이 선택된 상태에서는 추가 문항을 설정 할 수 없습니다.',
          variant: 'warning',
        }),
      );
      return;
    }
    dispatch({
      type: 'handleQuestion',
      payload: {
        type: 'inputs',
        index: questions.length,
        question: '',
        questionType,
        actionType: 'add',
      },
    });
  };

  const handleDelete = (questionId: string) => {
    dispatch({
      type: 'handleQuestion',
      payload: {
        type: 'inputs',
        questionId,
        actionType: 'delete',
      },
    });
  };

  const handleCheck = () => {
    onChange('stepper3', {
      questions: [],
      noAdditional: true,
    });
    handleCancel();
  };
  const handleCancel = () => {
    dispatchApp(closeDialog());
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checkValue = event.target.checked;
    if (checkValue) {
      dispatchApp(
        openDialog({
          content: (
            <InfoDialog
              title="추가문항 없음"
              contents="'추가문항 없음'을 선택하면 지금까지 작성한 문항들이 삭제됩니다. 그래도 진행하시겠습니까?"
              buttonInfo={
                <Stack direction="row" spacing={2} alignContent="center">
                  <Button variant="outlined" color="primary" onClick={handleCancel}>
                    취소
                  </Button>
                  <Button variant="contained" color="primary" onClick={handleCheck}>
                    확인
                  </Button>
                </Stack>
              }
            />
          ),
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
        }),
      );
    } else {
      onChange('stepper3', {
        questions: checkValue ? [] : stepper3.questions,
        noAdditional: checkValue,
      });
    }
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ px: '1.625rem' }}>
        <Grid
          container
          spacing={2}
          sx={{
            mt: '1.5rem',
            width: '100%',
            display: 'flex',
            justifyContent: { xs: 'flex-start', lg: 'center' },
          }}
        >
          <Box sx={{ width: '100%', maxHeight: 'calc(100vh - 430px)', overflow: 'auto' }}>
            <Grid item xs={12} sx={{ pl: '0px!important', pt: '0px!important' }}>
              {questions.map((question, index) => {
                const QuestionType = questionTypeMap[question.questionType];
                return (
                  <Box key={index} sx={{ mt: 2 }}>
                    <QuestionType
                      type="inputs"
                      sort={index}
                      component={question}
                      handleRemove={() => handleDelete(question.questionId)}
                    />
                  </Box>
                );
              })}
            </Grid>
          </Box>
          <Grid item xs={12} sx={{ pl: '0px!important', pt: '0px!important' }}>
            <Box>
              <Paper elevation={3} sx={{ p: 2, m: 2, display: 'flex', justifyContent: 'center' }}>
                <Stack
                  direction="column"
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <Box sx={{ typography: 's16w800' }}>추가 문항의 유형을 선택해주세요.</Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ width: '100%', display: 'flex', justifyContent: 'space-evenly' }}
                  >
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 105, width: 'auto' }}
                        onClick={() => handleAdd('OC')}
                      >
                        객관식
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 105, width: 'auto' }}
                        onClick={() => handleAdd('SA')}
                      >
                        단답형
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 105, width: 'auto' }}
                        onClick={() => handleAdd('DA')}
                      >
                        서술형
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 105, width: 'auto' }}
                        onClick={() => handleAdd('MC')}
                      >
                        체크박스
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 105, width: 'auto' }}
                        onClick={() => handleAdd('LK')}
                      >
                        리커트
                      </Button>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={noAdditional ?? false}
                          onChange={handleCheckChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label="추가문항 없음"
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
}

export default memo(Stepper3);
